/**
 * Format a number with comma separators.
 * Example: 1234567 -> "1,234,567"
 */
export function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

/**
 * Format bytes as megabytes with one decimal place.
 * Example: 1572864 -> "1.5 MB"
 */
export function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

/**
 * Format a gigs value as gigabytes with one decimal place.
 * Example: 2.345 -> "2.3 GB"
 */
export function formatGigs(gigs: number): string {
  return `${gigs.toFixed(1)} GB`;
}

/**
 * Convert an ISO date string to a human-readable relative time.
 * Returns "Never" for null/empty values.
 */
export function getRelativeTime(isoDate: string | null): string {
  if (!isoDate) return 'Never';

  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  if (diffMs < 0) return 'Just now';

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min${diffMinutes === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  if (diffDays < 30) return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
  return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
}

/**
 * Format an ISO date string as a full date-time for use in tooltips.
 * Example: "Monday, March 01, 2025 at 2:30 PM"
 * Returns "Never" for null/empty values.
 */
export function getFullDateTime(isoDate: string | null): string {
  if (!isoDate) return 'Never';

  const date = new Date(isoDate);

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
